import { LightningElement, api } from 'lwc';
import { fetchAuthSession } from 'aws-amplify/auth';

import { getLabelsByLanguage } from '../../../utils/languageUtil';
import { API_BASE_URL } from '../../../utils/awsUtils';

import {
    MATERIAL_ICONS, 
    LISTING_ICONS_BY_VALUE, 
    LUCIDE_ICONS
} from '../../../utils/iconMapper';


import { AMENITIES, AMENITIES_2 } from '../../../utils/optionsUtil';

import { post } from 'aws-amplify/api';

const GUEST_WAIVER_VERSION = '2026-08-04-v1';
const GUEST_WAIVER_TEXT = `# GETWET

## GUEST ASSUMPTION OF RISK, RELEASE OF LIABILITY, AND BOOKING PARTY AGREEMENT

Last Updated: August 4, 2026

This Guest Assumption of Risk, Release of Liability, and Booking Party Agreement ("Agreement") applies to reservations made through the GetWet platform ("GetWet") for access to swimming pools, properties, amenities, and related areas made available by property owners or other authorized hosts ("Hosts").

By accepting this Agreement and proceeding with a reservation, the individual making the reservation ("Booking Guest") acknowledges and agrees to the following terms.

### 1. ACKNOWLEDGMENT AND ASSUMPTION OF RISK

The Booking Guest understands that swimming, entering or exiting a swimming pool, using pool equipment or amenities, and being present in or around a swimming pool or private property involve inherent and other risks of injury.

Such risks may include, without limitation, slips, trips, falls, drowning or near-drowning, collisions, diving-related injuries, exposure to water or weather conditions, reactions to pool chemicals, injuries involving pool equipment or surrounding surfaces, and injuries resulting from the actions of other persons at the Property.

The Booking Guest voluntarily chooses to enter and use the Property with knowledge of these risks and voluntarily assumes all inherent and reasonably foreseeable risks associated with such use, to the fullest extent permitted by applicable law.

### 2. RELEASE AND WAIVER OF CLAIMS

TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE BOOKING GUEST, ON BEHALF OF THE BOOKING GUEST AND THE BOOKING GUEST'S HEIRS, ESTATE, PERSONAL REPRESENTATIVES, AND PERMITTED ASSIGNS, RELEASES AND DISCHARGES:

1. the owner, operator, Host, tenant, or other lawful provider of the Property; and

2. GetWet, its operating entity, and its respective owners, officers, directors, employees, contractors, agents, affiliates, successors, and assigns,

collectively, the "Released Parties," from claims for bodily injury, property damage, or other loss arising from the ordinary risks associated with the Booking Guest's voluntary use of the Property, including claims arising from the ordinary negligence of a Released Party, but only to the extent such claims may lawfully be released.

THIS RELEASE DOES NOT APPLY TO GROSS NEGLIGENCE, RECKLESS OR WILLFUL MISCONDUCT, INTENTIONAL WRONGDOING, FRAUD, OR ANY OTHER LIABILITY THAT CANNOT LAWFULLY BE WAIVED OR LIMITED.

### 3. RESPONSIBILITY FOR INVITED GUESTS

The Booking Guest is responsible for all persons whom the Booking Guest invites, permits, or otherwise causes to attend the reservation ("Invited Guests").

The Booking Guest agrees to:

(a) provide Invited Guests with access to the applicable GetWet guest terms, safety information, Property rules, and liability waiver before they enter or use the Property;

(b) ensure, to the extent required by GetWet, that Invited Guests review and accept the applicable terms before entering or using the Property;

(c) communicate all applicable Property rules, restrictions, warnings, and safety requirements to Invited Guests;

(d) not knowingly permit an Invited Guest to use the Property in violation of applicable law, Property rules, or GetWet policies; and

(e) accurately represent the number of persons expected to attend the reservation.

GetWet may provide the Booking Guest with a link or other method for sharing applicable terms and waiver information with Invited Guests.

The Booking Guest understands that accepting this Agreement does not, by itself, waive an independent legal claim belonging to another adult where applicable law requires that person's own consent.

### 4. NON-ACCEPTING OR UNREGISTERED GUESTS

If an Invited Guest enters or uses the Property without separately accepting GetWet's applicable guest terms or waiver where such acceptance is required, that person's presence does not expand the duties or obligations of GetWet or the Host beyond those imposed by applicable law.

The Booking Guest remains responsible for complying with this Agreement with respect to that Invited Guest and for ensuring that the Invited Guest follows all applicable Property rules and safety requirements.

Nothing in this Agreement is intended to waive another person's legal rights without that person's legally effective consent.

### 5. GUEST CONDUCT

The Booking Guest is responsible for exercising reasonable care while at the Property and agrees to take reasonable steps to ensure that Invited Guests do the same.

The Booking Guest and Invited Guests must comply with:

* applicable laws and regulations;
* posted warnings and safety instructions;
* GetWet policies applicable to the reservation;
* rules contained in the Property listing; and
* reasonable safety instructions provided by the Host.

Unsafe, reckless, unlawful, or prohibited use of the Property is not permitted.

### 6. SUPERVISION OF MINORS

The Booking Guest is responsible for ensuring that minors attending as part of the Booking Guest's party are appropriately supervised by a responsible adult.

Children and persons who cannot safely swim without assistance must be continuously supervised while in or around a swimming pool.

The Booking Guest must ensure that a sufficient number of responsible adults are present to supervise minors in the party.

Nothing in this Agreement is intended to waive or limit rights belonging to a minor where those rights cannot lawfully be waived by the Booking Guest, parent, guardian, or another person.

### 7. PROPERTY CONDITIONS

The Booking Guest acknowledges that each Property is independently owned, controlled, or operated by its Host.

Before using the Property, the Booking Guest is responsible for reviewing the listing, Property rules, restrictions, safety information, and disclosed conditions and for communicating applicable information to Invited Guests.

If the Booking Guest or an Invited Guest observes a condition reasonably believed to present an immediate safety hazard, use of the affected area or amenity should stop and the Host should be notified.

Nothing in this Agreement relieves a Host or GetWet of a legal obligation that cannot lawfully be waived.

### 8. GETWET'S ROLE

GetWet provides an online platform through which Hosts may offer Properties and Booking Guests may discover and reserve them.

Unless expressly stated otherwise, GetWet does not own, possess, operate, manage, supervise, or control Properties listed by Hosts and does not provide lifeguard or on-site supervision services.

Hosts are independently responsible for their Properties and for compliance with laws and regulations applicable to their operation and use.

Nothing in this Agreement creates an employment, agency, partnership, joint venture, or landlord-tenant relationship between GetWet and any Host or Guest.

### 9. INDEMNIFICATION

To the fullest extent permitted by applicable law, the Booking Guest agrees to indemnify and hold harmless the Released Parties from third-party claims, damages, liabilities, costs, or expenses, including reasonable attorneys' fees where recoverable by law, arising from:

(a) the Booking Guest's violation of this Agreement;

(b) intentional, reckless, or negligent acts or omissions of the Booking Guest;

(c) damage to the Property caused by the Booking Guest or an Invited Guest;

(d) the Booking Guest's violation of applicable law or Property rules; or

(e) the Booking Guest's failure to fulfill obligations expressly assumed under this Agreement concerning Invited Guests.

This provision does not require the Booking Guest to indemnify a Released Party for liability that applicable law prohibits that Released Party from transferring to the Booking Guest.

### 10. PERSONAL PROPERTY

The Booking Guest and Invited Guests are responsible for their personal belongings while at the Property.

To the fullest extent permitted by applicable law, the Released Parties are not responsible for loss, theft, or damage to personal property except to the extent caused by conduct for which liability cannot lawfully be excluded.

### 11. MEDICAL AND EMERGENCY RESPONSIBILITY

The Booking Guest is responsible for determining whether the Booking Guest is physically capable of safely participating in swimming and other activities at the Property.

The Booking Guest is responsible for encouraging Invited Guests to make the same determination for themselves.

In an emergency, the Booking Guest or another person at the Property should contact appropriate emergency services.

Unless expressly stated otherwise, GetWet does not provide medical services, emergency response services, lifeguards, or other on-site safety personnel.

### 12. PROPERTY RULES AND RESERVATION LIMITS

The Booking Guest agrees to comply with and take reasonable steps to ensure Invited Guests comply with all rules applicable to the reservation, including restrictions concerning occupancy, children, alcohol, smoking, pets, parties, noise, diving, food and beverages, glass containers, photography, commercial activity, and other activities where applicable.

The Booking Guest may not intentionally exceed the number of guests authorized by the reservation.

Material violation of Property rules may result in termination of access to the Property and other remedies available under GetWet's policies and applicable law.

### 13. NO GUARANTEE OF SAFETY

The Booking Guest understands that neither this Agreement nor information provided through GetWet constitutes a representation or guarantee that a Property is free from risk.

No listing description, photograph, safety feature, inspection, warning, rule, or other precaution can eliminate all risks associated with swimming pools and private properties.

### 14. SEVERABILITY

If any provision of this Agreement is determined to be invalid, illegal, or unenforceable, that provision shall be enforced to the maximum extent permitted by law or, where appropriate, severed from this Agreement.

The remaining provisions shall remain effective to the fullest extent permitted by applicable law.

### 15. RELATIONSHIP TO GETWET TERMS

This Agreement supplements GetWet's Terms of Service, Privacy Policy, booking policies, and the rules applicable to the reserved Property.

If GetWet's Terms of Service contain applicable governing-law, venue, arbitration, dispute-resolution, or other contractual provisions, those provisions shall apply to this Agreement to the extent permitted by law.

### 16. ELECTRONIC ACCEPTANCE AND RECORDS

By electronically accepting this Agreement, the Booking Guest intends to provide an electronic signature and agrees to be legally bound by this Agreement to the extent recognized by applicable law.

GetWet may maintain records concerning acceptance of this Agreement, including:

* Booking Guest account or user identifier;
* reservation or booking identifier;
* Property or listing identifier;
* version of this Agreement;
* date and time of acceptance; and
* other transaction information reasonably necessary to document acceptance.

### 17. BOOKING GUEST ACKNOWLEDGMENT

BY ACCEPTING THIS AGREEMENT, THE BOOKING GUEST ACKNOWLEDGES THAT THE BOOKING GUEST:

* HAS READ AND UNDERSTANDS THIS AGREEMENT;
* UNDERSTANDS THAT SWIMMING AND POOL-RELATED ACTIVITIES INVOLVE RISKS OF SERIOUS INJURY;
* VOLUNTARILY ASSUMES THE RISKS DESCRIBED IN THIS AGREEMENT;
* UNDERSTANDS THAT THIS AGREEMENT RELEASES CERTAIN LEGAL CLAIMS TO THE EXTENT PERMITTED BY LAW;
* ACCEPTS RESPONSIBILITY FOR COMPLYING WITH THE RULES APPLICABLE TO THE RESERVATION;
* AGREES TO COMMUNICATE APPLICABLE RULES, SAFETY INFORMATION, AND TERMS TO INVITED GUESTS; AND
* AGREES TO ENSURE, TO THE EXTENT REQUIRED BY GETWET, THAT INVITED GUESTS REVIEW AND ACCEPT THE APPLICABLE GUEST TERMS BEFORE ENTERING OR USING THE PROPERTY.

## ELECTRONIC ACCEPTANCE

I have read and agree to the GetWet Guest Assumption of Risk, Release of Liability, and Booking Party Agreement. I understand my responsibilities concerning the guests I invite to this reservation.

Booking Guest: Captured from GetWet Account
Booking ID: Automatically Recorded
Property/Listing ID: Automatically Recorded
Number of Guests: Captured from Reservation
Agreement Version: Automatically Recorded
Date and Time Accepted: Automatically Recorded`;


// Prepare house rules to feed to LLM to help user with questions
function buildLLMHouseRules(houseRules) {
    /* House Rules Structure:
        houseRules = [
            {
                key: "house-rule-underscore_separated_field_name"
                label: "rule_short_label"
                value: "underscore_separated_field_name"
                isRule: T/F 
                isAllowed: T/F 
            }
        ]
    */
    
    // Prepare listing-specific rules for the LLM 
    const llmHouseRules = houseRules.map(rule => {
        // Handle Rule Label (always house-rule- + rule_name_allowed)
        const ruleNameRaw = rule.key;
        const cleanedRuleName = ruleNameRaw
            .replace('house-rule-', '')
            .replace('_allowed', '');
        const label = cleanedRuleName.replace('_', ' ');

        // Handle rule value (T/F) 
        const value = rule.isAllowed;

        return {label, value}
    });

    return llmHouseRules;
}

// Check listing for number of allowed guests (default is 1-50)
function buildGuestOptions(min=1, max=50, priceBuckets=null) {
    const guestOptions = [];
    const buckets = Array.isArray(priceBuckets) ? priceBuckets : [];
    const hasBuckets = buckets.length > 0;

    for (let i = min; i <= max; i++) {
        if (hasBuckets && !buckets.some(bucket => i >= bucket.min_guests && i <= bucket.max_guests)) {
            continue;
        }

        guestOptions.push(
            {
                label: `${i} ${getLabelsByLanguage('guests').toLowerCase()}`,
                value: i,
            }
        )
    }

    return guestOptions;
}

function normalizePriceBuckets(priceBuckets = []) {
    const normalizePriceCents = (value) => {
        const price = Number(value);

        if (!Number.isFinite(price)) return price;

        return price > 0 && price < 1000
            ? Math.round(price * 100)
            : Math.round(price);
    };

    return (priceBuckets || [])
        .map((bucket) => {
            if (
                bucket &&
                Object.prototype.hasOwnProperty.call(bucket, 'min_guests') &&
                Object.prototype.hasOwnProperty.call(bucket, 'max_guests')
            ) {
                return {
                    min_guests: Number(bucket.min_guests),
                    max_guests: Number(bucket.max_guests),
                    price_cents: normalizePriceCents(bucket.price_cents)
                };
            }

            const [key, value] = Object.entries(bucket || {})[0] || [];
            const match = key?.match(/^Guest_Price_Bucket_(\d+)_(\d+)$/);

            if (!match) return null;

            return {
                min_guests: Number(match[1]),
                max_guests: Number(match[2]),
                price_cents: normalizePriceCents(value)
            };
        })
        .filter(bucket =>
            bucket &&
            Number.isFinite(bucket.min_guests) &&
            Number.isFinite(bucket.max_guests) &&
            Number.isFinite(bucket.price_cents)
        )
        .sort((a, b) => a.min_guests - b.min_guests || a.max_guests - b.max_guests);
}

function formatCurrencyFromCents(cents) {
    const amount = Number(cents);

    if (!Number.isFinite(amount)) {
        return '$0.00';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount / 100);
}

function extractEventValue(message) {
    return message?.detail?.value ??
        message?.detail?.selection ??
        message?.detail ??
        null;
}

function parseReservationDateTime(date, timeLabel) {
    if (!date || !timeLabel) return null;

    const match = String(timeLabel).match(/^(\d{1,2}):(\d{2})\s*([AP])\.?M\.?$/i);

    if (!match) return null;

    let hour = Number(match[1]);
    const minute = Number(match[2]);
    const period = match[3].toUpperCase();

    if (period === 'P' && hour !== 12) hour += 12;
    if (period === 'A' && hour === 12) hour = 0;

    const dateObj = new Date(`${date}T00:00:00`);
    dateObj.setHours(hour, minute, 0, 0);

    return dateObj;
}



function formatTime(hour, minutes) {
    const period = hour >= 12 ? 'P.M.' : 'A.M.';
    let h = hour % 12;
    if (h === 0) h = 12;

    const mm = minutes === 0 ? '00' : '30';
    return `${h}:${mm} ${period}`;
}

function createTimeArr(startInt = 8, endInt = 20) {
    const timesArr = [];
    for (let h = startInt; h <= endInt; h++) {
        for (const m of [0, 30]) {
            if (h === 20 && m === 30) break;

            const label = formatTime(h, m);

            timesArr.push({
                label,
                value: label
            });
        }
    }
    return timesArr;
}

const DESTINATION_OPTIONS = [
    {label: 'Durango, MX', value: 'durango:durango:mx'},
    {label: 'CDMX (Ciudad de Mexico), MX', value: 'cdmx:mexico:mx'},
    {label: 'Chicago, IL', value: 'chicago:il:usa'}
];
function getEndTimeOptions(startIdx, options, minStay = 30, maxStay = null) {
    const startIdxSkip = minStay / 30;
    const maxIdxSkip = maxStay
        ? maxStay / 30
        : options.length;

    if (startIdx === -1) {
        return options;
    }
    if (startIdx === options.length - 1) {
        return [];
    }

    return options.slice(startIdx + startIdxSkip, maxIdxSkip);
}

function normalizePhotos(photos = []) {
    return (photos || []).map((photo, index) => ({
        ...photo,
        loading: !!photo?.s3_key_url,
        modalIndex: index
    }));
}

const AMENITY_OPTIONS_BY_VALUE = [
    ...AMENITIES,
    ...AMENITIES_2.reduce((items, group) => (
        items.concat(Array.isArray(group.items) ? group.items : [])
    ), [])
].reduce((optionsByValue, amenity) => {
    if (amenity?.value) {
        optionsByValue[amenity.value] = amenity;
    }
    return optionsByValue;
}, {});

const HOUSE_RULES = [
    {field: 'infants_allowed', label: 'Infants', icon: 'children'},
    {field: 'infants_free', label: 'Infants Free', icon: 'children'},
    {field: 'alcohol_allowed', label: 'Alcohol', icon: 'alcohol'},
    {field: 'smoking_allowed', label: 'Smoking', icon: 'smoking'},
    {field: 'pets_allowed', label: 'Pet Friendly', icon: 'pets'},
    {field: 'parties_allowed', label: 'Parties', icon: 'music'},
    {field: 'loud_music_allowed', label: 'Music', icon: 'music'},
    {field: 'glass_containers_allowed', label: 'Glass Containers', icon: 'alcohol'},
    {field: 'outside_food_allowed', label: 'Food', icon: 'food'},
    {field: 'outside_drinks_allowed', label: 'Drinks', icon: 'alcohol'},
    {field: 'photography_allowed', label: 'Photos', icon: 'photos'},
    {field: 'commercial_filming_allowed', label: 'Commercial Filming', icon: 'photos'}
];

const FEATURED_RULES_AND_AMENITIES = [
    'Alcohol',
    'Food',
    'Music',
    'Bluetooth Speakers',
    'Wi-Fi',
    'Towels',
    'Shower',
    'Photos',
    'Pet Friendly',
    'Smoking',
    'Heated Pool'
];

const FEATURED_AMENITY_VALUE_ALIASES = {
    Towels: ['Towels Included', 'Towels Provided'],
    Shower: ['Outdoor Shower']
};

function normalizeAmenityValues(amenities) {
    const values = [];
    const addValue = (value) => {
        const normalizedValue = typeof value === 'string' ? value.trim() : '';
        if (normalizedValue) {
            values.push(normalizedValue);
        }
    };
    const visitAmenity = (amenity) => {
        if (typeof amenity === 'string') {
            addValue(amenity);
            return;
        }

        if (Array.isArray(amenity)) {
            amenity.forEach(visitAmenity);
            return;
        }

        if (!amenity || typeof amenity !== 'object') {
            return;
        }

        if (Array.isArray(amenity.items)) {
            const hasSelectionState = amenity.items.some(item =>
                item && typeof item === 'object' && (
                    'selected' in item ||
                    'checked' in item ||
                    'isSelected' in item
                )
            );
            const selectedItems = hasSelectionState
                ? amenity.items.filter(item => item?.selected || item?.checked || item?.isSelected)
                : amenity.items;

            selectedItems.forEach(visitAmenity);
            return;
        }

        addValue(amenity.value);
    };

    visitAmenity(amenities);
    return [...new Set(values)];
}   

function createDisplayItem({
    key,
    label,
    value,
    amenity = null,
    isRule = false,
    isAllowed = null,
    ruleIconName = null
}) {
    const resolvedRuleIconName = isRule && !isAllowed
        ? `no_${ruleIconName}`
        : ruleIconName;
    const rawMaterialIcon = resolvedRuleIconName
        ? MATERIAL_ICONS[resolvedRuleIconName]
        : amenity?.isMaterialIcon && amenity.icon
            ? MATERIAL_ICONS[amenity.icon]
            : null;

    if (isRule && !isAllowed && !rawMaterialIcon) {
        return null;
    }

    const materialIcon = rawMaterialIcon
        ? {...rawMaterialIcon, viewBox: rawMaterialIcon.viewBox || '0 -960 960 960'}
        : null;
    const lucideIcon = amenity?.isLucideIcon && amenity.icon
        ? LUCIDE_ICONS[amenity.icon]
        : null;

    return {
        key,
        label: label || amenity?.label || value,
        value,
        isRule,
        isAllowed,
        labelClass: 'amenity-item__label',
        hasMaterialIcon: !!materialIcon,
        materialIcon,
        hasLucideIcon: !!lucideIcon,
        lucideIcon: lucideIcon
            ? {
                viewBox: lucideIcon.viewBox || lucideIcon.viewBow || '0 0 24 24',
                strokeWidth: lucideIcon.stroke_width || '2',
                strokeLinecap: lucideIcon.stroke_linecap || 'round',
                strokeLinejoin: lucideIcon.stroke_linejoin || 'round',
                paths: (lucideIcon.paths || []).map((path, pathIndex) => ({
                    key: `${key}-lucide-path-${pathIndex}`,
                    d: path
                }))
            }
            : null
    };
}

function buildAmenityOptions(amenities) {
    return normalizeAmenityValues(amenities).map((value, index) => {
        const amenity = AMENITY_OPTIONS_BY_VALUE[value] || {label: value, value};

        return createDisplayItem({
            key: `amenity-${index}-${value}`,
            value,
            amenity
        });
    });
}

function buildHouseRuleOption(rule, listing = {}) {
    return createDisplayItem({
        key: `house-rule-${rule.field}`,
        label: listing?.[rule.field] === true ? rule.label : `${rule.label} not allowed`,
        value: rule.field,
        isRule: true,
        isAllowed: listing?.[rule.field] === true,
        ruleIconName: rule.icon
    });
}

function buildHouseRuleOptions(listing = {}) {
    return HOUSE_RULES.map(rule => buildHouseRuleOption(rule, listing)).filter(Boolean);
}

function buildAmenityCategories(amenities) {
    const selectedValues = new Set(normalizeAmenityValues(amenities));
    const groupedValues = new Set();
    const categories = AMENITIES_2.map((group, groupIndex) => ({
        key: `amenity-category-${groupIndex}`,
        category: group.category,
        items: (Array.isArray(group.items) ? group.items : [])
            .filter((amenity) => {
                const isSelected = selectedValues.has(amenity.value);
                if (isSelected) {
                    groupedValues.add(amenity.value);
                }
                return isSelected;
            })
            .map((amenity, amenityIndex) => createDisplayItem({
                key: `amenity-category-${groupIndex}-${amenityIndex}-${amenity.value}`,
                value: amenity.value,
                amenity
            }))
    })).filter(group => group.items.length);

    const uncategorizedItems = [...selectedValues]
        .filter(value => !groupedValues.has(value))
        .map((value, index) => createDisplayItem({
            key: `amenity-category-other-${index}-${value}`,
            value,
            amenity: AMENITY_OPTIONS_BY_VALUE[value] || {label: value, value}
        }));

    return uncategorizedItems.length
        ? [...categories, {key: 'amenity-category-other', category: 'Other', items: uncategorizedItems}]
        : categories;
}

function buildFeaturedRulesAndAmenities(listing = {}) {
    const selectedValues = new Set(normalizeAmenityValues(listing?.amenities));
    const rulesByLabel = Object.fromEntries(
        HOUSE_RULES.map((rule) => [
            rule.label,
            buildHouseRuleOption(rule, listing)
        ]).filter(([, ruleItem]) => ruleItem)
    );
    const amenityItemsByValue = Object.fromEntries(
        buildAmenityOptions(listing?.amenities).map(amenity => [amenity.value, amenity])
    );

    return FEATURED_RULES_AND_AMENITIES.map((label) => {
        if (rulesByLabel[label]) {
            return {
                ...rulesByLabel[label],
                key: `featured-${rulesByLabel[label].key}`
            };
        }

        const valuesToMatch = FEATURED_AMENITY_VALUE_ALIASES[label] || [label];
        const matchedValue = valuesToMatch.find(value => selectedValues.has(value));

        if (!matchedValue || !amenityItemsByValue[matchedValue]) {
            return null;
        }

        return {
            ...amenityItemsByValue[matchedValue],
            key: `featured-${amenityItemsByValue[matchedValue].key}`,
            label
        };
    }).filter(Boolean);
}

function removeLoadingFlagPhoto(cmp, s3Key) {
    if (s3Key === 'primary-photo') {
        cmp._primaryImageLoading = false;
        return;
    }

    cmp._previewPhotos = cmp._previewPhotos.map((photo) => (
        photo.s3_key === s3Key
            ? {...photo, loading: false}
            : photo
    ));

    cmp._allPhotos = cmp._allPhotos.map((photo) => (
        photo.s3_key === s3Key
            ? {...photo, loading: false}
            : photo
    ));
}

function formatHostSince(dateStr) {
    const d = new Date(dateStr);
    const now = new Date();

    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
        return 'New host';
    }

    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
        return `${diffMonths} month${diffMonths > 1 ? 's' : ''} hosting`;
    }

    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears} year${diffYears > 1 ? 's' : ''} hosting`;
}



const BOOKING_TRACKER_KEYS = [
    'summaryComplete',
    'guestDetailsComplete',
    'paymentInfoComplete'
];
function updateBookingTracker(bookingTracker, blockNextStep = false) {
    const stepIdx = BOOKING_TRACKER_KEYS.findIndex(
        (key) => !bookingTracker[key]
    );

    const currentStep = stepIdx === -1
            ? BOOKING_TRACKER_KEYS.length
            : stepIdx;

    const stepIsDisabled = (idx) => {
        return blockNextStep
            ? idx >= currentStep
            : idx > currentStep;
    };

    return Object.fromEntries(
        Object.entries(bookingTracker).map(([key], index) => {
            return [key, !stepIsDisabled(index)];
        })
    );
}



function getIcons(fieldName, val) {
    const variants = [
        'framed-pool',
        'full-pool',
        'hot-tub',
        'cold-plunge', 
        'logo',
        'guests',
    ];

    if (fieldName === 'pool_type') {
        switch (val) {
            case 'Full Pool':
                return 'full-pool';
            case 'Framed Pool':
                return 'framed-pool';
            case 'Hot Tub':
                return 'hot-tub';
            case 'Cold Plunge':
                return 'cold-plunge';
            default: 
                return null;
        }
    }
}



export default class getWetListingPage extends LightningElement {
    materialIcons = MATERIAL_ICONS;
    
    
    destinationOptions = DESTINATION_OPTIONS;

    _listing;
    _listingId;
    _isLoading = false;
    _isLoaded = false;
    _primaryImageLoading = false;

    _previewPhotos = [];
    _allPhotos = [];
    _amenities = [];
    _amenityCategories = [];
    _houseRules = [];
    _featuredRulesAndAmenities = [];
    _totalPhotoCount = 0;
    _hasLoadedAllPhotos = false;
    _isFetchingAllPhotos = false;

    _isGalleryModalOpen = false;
    _galleryModalMode = 'grid';
    _galleryModalLoading = false;
    _activePhotoIndex = 0;

    _isHostMessageModalOpen = false;
    _isRulesAmenitiesModalOpen = false;
    _isSendingHostMessage = false;
    _hostMessageBody = '';
    _isBookingSummaryOpen = false;
    _isGuestWaiverModalOpen = false;
    _hasAcceptedGuestWaiver = false;
    _bookingSummary = null;

    reservationTimes = createTimeArr();
    guestOptions = [];

    _reservation = {
        date: null,
        start_time: null,
        end_time: null,
        guests: 1,
    }

    _listingIcons = {};
    async loadListing(listingId) {
        this._isLoading = true;
        this._isLoaded = false;

        try {
            if (!listingId) {
                this._listing = null;
                this._previewPhotos = [];
                this._allPhotos = [];
                this._amenities = [];
                this._amenityCategories = [];
                this._houseRules = [];
                this._featuredRulesAndAmenities = [];
                this._totalPhotoCount = 0;
                this._hasLoadedAllPhotos = false;
                return;
            }

            const listing = await this.fetchListing(listingId);
            const normalizedPhotos = normalizePhotos(listing?.listing_photos);
            const priceBuckets = normalizePriceBuckets(listing?.listing_price_bucket);
            const amenities = buildAmenityOptions(listing?.amenities);
            const amenityCategories = buildAmenityCategories(listing?.amenities);
            const houseRules = buildHouseRuleOptions(listing);
            const featuredRulesAndAmenities = buildFeaturedRulesAndAmenities(listing);
            this._primaryImageLoading = !!normalizedPhotos?.[0]?.s3_key_url;
            this._previewPhotos = normalizedPhotos;
            this._allPhotos = normalizedPhotos;
            this._amenities = amenities;
            this._amenityCategories = amenityCategories;
            this._houseRules = houseRules;

            const llmHouseRules = buildLLMHouseRules(houseRules);

            this._featuredRulesAndAmenities = featuredRulesAndAmenities;
            this._totalPhotoCount = listing?.total_photo_count || normalizedPhotos.length;
            this._hasLoadedAllPhotos = normalizedPhotos.length >= this._totalPhotoCount;

            const hostSince = formatHostSince(listing.host_created_at);
            
            this._listing = {
                ...listing,
                listing_photos: normalizedPhotos,
                listing_price_bucket: priceBuckets,
                amenities: normalizeAmenityValues(listing?.amenities),
                host_since: hostSince,
            };

            this.guestOptions = buildGuestOptions(
                Number(this._listing.min_guests || 1),
                Number(this._listing.max_guests || 1),
                priceBuckets
            );
            this.initializeReservationGuests();

            const listingIconNames = {
                water_system: LISTING_ICONS_BY_VALUE[listing.water_system],
                environment_type: LISTING_ICONS_BY_VALUE[listing.environment_type],
                pool_type: LISTING_ICONS_BY_VALUE[listing.pool_type],
                restroom_type: LISTING_ICONS_BY_VALUE[listing.restroom_type],
            };

            this._listingIcons = Object.fromEntries(
                Object.entries(listingIconNames).map(([key, value]) => {
                    return [
                        key,
                        value?.isMaterialIcon
                            ? MATERIAL_ICONS[value.iconName]
                            : value
                    ];
                })
            );

        } catch (error) {
            this.showToast('Error loading listing', error?.message || error, 'error');
        } finally {
            this._isLoading = false;
            this._isLoaded = true;
            this.tryOpenMessageHost();
        }
    }
    async fetchListing(listingId, includeAllPhotos = false) {
        const params = new URLSearchParams({ listingId });

        if (includeAllPhotos) {
            params.set('includeAllPhotos', 'true');
        }

        const res = await fetch(`${API_BASE_URL}/public/listing?${params.toString()}`, {
            method: 'GET'
        });

        if (!res.ok) {
            const errText = await res.text();
            throw new Error(`Failed to fetch listing: ${res.status} ${errText}`);
        }

        const data = await res.json();
        return data?.listing ?? {};
    }
    async fetchAllPhotosIfNeeded() {
        if (this._hasLoadedAllPhotos || this._isFetchingAllPhotos || !this._listingId) {
            return;
        }

        this._isFetchingAllPhotos = true;
        this._galleryModalLoading = true;

        try {
            const listing = await this.fetchListing(this._listingId, true);
            const normalizedPhotos = normalizePhotos(listing?.listing_photos);

            this._allPhotos = normalizedPhotos;
            this._totalPhotoCount = listing?.total_photo_count || normalizedPhotos.length;
            this._hasLoadedAllPhotos = normalizedPhotos.length >= this._totalPhotoCount;
        } catch (error) {
            this.showToast('Error loading gallery photos', error?.message || error, 'error');
        } finally {
            this._isFetchingAllPhotos = false;
            this._galleryModalLoading = false;
        }
    }
    
    connectedCallback() {
        this._boundHandleWindowKeydown = this.handleWindowKeydown.bind(this);
        window.addEventListener('keydown', this._boundHandleWindowKeydown);
    }
    disconnectedCallback() {
        window.removeEventListener('keydown', this._boundHandleWindowKeydown);
        document.body.style.overflow = '';
    }
    renderedCallback() {
        document.body.style.overflow = this._isGalleryModalOpen || this._isRulesAmenitiesModalOpen
            ? 'hidden'
            : '';
    }



    handleImageLoaded(event) {
        const s3Key = event.target.dataset.id;
        removeLoadingFlagPhoto(this, s3Key);
    }
    handleImageError(event) {
        const s3Key = event.target.dataset.id;
        removeLoadingFlagPhoto(this, s3Key);
    }
    handleReservationDate(message) {
        const value = extractEventValue(message);
        this._reservation = {
            ...this._reservation,
            date: value
        };
    }

    handleBackToHome() {
        this.dispatchEvent(new CustomEvent('backtohome', {
            bubbles: true,
            composed: true
        }));
    }

    @api isAuthenticated = false;
    _paymentCancelled = false;
    _paymentCancelledDismissed = false;
    _openMessageHost = false;
    _hasHandledOpenMessageHost = false;

    @api
    set paymentCancelled(value) {
        this._paymentCancelled = value === true || ['1', 'true', 'yes'].includes(
            String(value || '').toLowerCase()
        );

        if (this._paymentCancelled) {
            this._paymentCancelledDismissed = false;
        }
    }
    get paymentCancelled() {
        return this._paymentCancelled;
    }

    @api
    set openMessageHost(value) {
        this._openMessageHost = value === true || ['1', 'true', 'yes'].includes(
            String(value || '').toLowerCase()
        );

        if (this._openMessageHost) {
            this._hasHandledOpenMessageHost = false;
            this.tryOpenMessageHost();
        }
    }
    get openMessageHost() {
        return this._openMessageHost;
    }

    @api
    set selectedGuestCount(val) {
        const parsed = Number(val);
        this._selectedGuestCount = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        this.initializeReservationGuests();
    }
    get selectedGuestCount() {
        return this._selectedGuestCount;
    }

    @api
    set selectedDate(val) {
        this._selectedDate = val || null;
        if (val) {
            this._reservation = {
                ...this._reservation,
                date: val
            };
        }
    }
    get selectedDate() {
        return this._selectedDate;
    }

    @api
    set listingId(val) {
        this._listingId = val;
        Promise.resolve().then(async () => {
            await this.loadListing(val);
        });
    }
    get listingId() {
        return this._listingId;
    }


    get previewPrimaryPhoto() {
        return this._previewPhotos[0] || null;
    }
    get previewSecondaryPhotos() {
        return this._previewPhotos.slice(1, 5);
    }
    get hasPreviewSecondaryPhotos() {
        return this.previewSecondaryPhotos.length > 0;
    }


    get galleryPhotos() {
        return this._hasLoadedAllPhotos ? this._allPhotos : this._previewPhotos;
    }
    get galleryPhotosForGrid() {
        return this.galleryPhotos.map((photo, index) => ({
            ...photo,
            modalIndex: index
        }));
    }
    get activePhoto() {
        return this.galleryPhotos[this._activePhotoIndex] || null;
    }
    get galleryCountLabel() {
        const total = this._allPhotos?.length ?? this._listing?.listing_photos?.length + 1;

        if (this.isViewerMode) {
            return `${this._activePhotoIndex + 1} / ${total}`;
        }

        return `${total} Photos`;
    }
    get isViewerMode() {
        return this._galleryModalMode === 'viewer';
    }
    get isGridMode() {
        return this._galleryModalMode === 'grid';
    }
    get canNavigateGallery() {
        return this.galleryPhotos.length > 1;
    }


    get priceBuckets() {
        return this._listing?.listing_price_bucket || [];
    }
    get lowestPriceBucket() {
        return this.priceBuckets.reduce((lowest, bucket) => {
            if (!lowest) return bucket;
            return bucket.price_cents < lowest.price_cents ? bucket : lowest;
        }, null);
    }
    get selectedPriceBucket() {
        return this.findPriceBucketForGuests(this._reservation.guests);
    }
    get selectedPricePerGuestCents() {
        return this.selectedPriceBucket?.price_cents ?? this.lowestPriceBucket?.price_cents ?? null;
    }


    get bookingCardTitle() {
        if (!Number.isFinite(Number(this.selectedPricePerGuestCents))) {
            return 'Select guests';
        }
        return `${formatCurrencyFromCents(this.selectedPricePerGuestCents)} / hr`;
    }
    get bookingSummaryDate() {
        return this._bookingSummary?.date || '';
    }
    get bookingSummaryTimeRange() {
        if (!this._bookingSummary) return '';
        return `${this._bookingSummary.startTimeLabel} - ${this._bookingSummary.endTimeLabel}`;
    }
    get bookingSummaryGuestCount() {
        return this._bookingSummary?.guestCount || 0;
    }
    get bookingSummaryPricePerGuest() {
        return formatCurrencyFromCents(this._bookingSummary?.pricePerGuestCents || 0);
    }
    get bookingSummaryBasePrice() {
        return formatCurrencyFromCents(this._bookingSummary?.basePriceCents || 0);
    }
    get bookingSummaryPlatformFee() {
        return formatCurrencyFromCents(this._bookingSummary?.platformFeeCents || 0);
    }
    get bookingSummaryTaxes() {
        return formatCurrencyFromCents(this._bookingSummary?.taxCents || 0);
    }
    get bookingSummaryTotal() {
        return formatCurrencyFromCents(this._bookingSummary?.totalPriceCents || 0);
    }
    get guestWaiverVersion() {
        return GUEST_WAIVER_VERSION;
    }
    get guestWaiverText() {
        return GUEST_WAIVER_TEXT;
    }
    get isContinueToPaymentDisabled() {
        return !this._hasAcceptedGuestWaiver;
    }


    initializeReservationGuests() {
        if (!this._listing) return;

        const selectedGuestCount = Number(this._selectedGuestCount);
        const selectedBucket = this.findPriceBucketForGuests(selectedGuestCount);
        const defaultBucket = selectedBucket || this.lowestPriceBucket;
        const defaultGuests = selectedBucket
            ? selectedGuestCount
            : defaultBucket?.min_guests || Number(this._listing.min_guests || 1);

        this._reservation = {
            ...this._reservation,
            guests: defaultGuests
        };
    }
    findPriceBucketForGuests(guestCount) {
        const guests = Number(guestCount);

        if (!Number.isFinite(guests)) return null;

        return this.priceBuckets.find(bucket =>
            guests >= bucket.min_guests && guests <= bucket.max_guests
        ) || null;
    }



    endTimeOptions = [];
    handleStartTime(message) {
        const value = extractEventValue(message);

        const matchedIdx = this.reservationTimes.findIndex((opt) => opt.value === value);
        this.endTimeOptions = getEndTimeOptions(matchedIdx, this.reservationTimes);
        if (this._reservation.end_time) {
            const endIdx = this.reservationTimes.findIndex((opt) => opt.value === this._reservation.end_time);
            if (endIdx > matchedIdx) {
                this._reservation = {
                    ...this._reservation,
                    start_time: value,
                    end_time: null
                };
            }
        }

        this._reservation = {
            ...this._reservation,
            start_time: value,
        };
    }
    handleEndTime(message) {
        const value = extractEventValue(message);
        this._reservation = {
            ...this._reservation,
            end_time: value
        };
    }
    handleGuests(message) {
        const value = Number(extractEventValue(message));

        this._reservation = {
            ...this._reservation,
            guests: Number.isFinite(value) ? value : null
        };
    }
    validateReservation() {
        const date = this._reservation.date;
        const startTime = this._reservation.start_time;
        const endTime = this._reservation.end_time;
        const guestCount = Number(this._reservation.guests);
        const maxGuests = Number(this._listing?.max_guests);
        const startDateTime = parseReservationDateTime(date, startTime);
        const endDateTime = parseReservationDateTime(date, endTime);
        const priceBucket = this.findPriceBucketForGuests(guestCount);
        const pricePerGuestCents = Number(priceBucket?.price_cents);

        if (!date) {
            return 'Select a date.';
        }
        if (!startTime) {
            return 'Select a start time.';
        }
        if (!endTime) {
            return 'Select an end time.';
        }
        if (!startDateTime || !endDateTime || endDateTime <= startDateTime) {
            return 'End time must be after start time.';
        }
        if (!Number.isFinite(guestCount) || guestCount < 1) {
            return 'Select the number of guests.';
        }
        if (Number.isFinite(maxGuests) && guestCount > maxGuests) {
            return `Guest count cannot exceed ${maxGuests}.`;
        }
        if (!priceBucket) {
            return 'No valid price bucket was found for that guest count.';
        }
        if (!Number.isFinite(pricePerGuestCents) || pricePerGuestCents < 0) {
            return 'The selected price per guest is invalid.';
        }

        return null;
    }


    buildBookingSummary() {
        const guestCount = Number(this._reservation.guests);
        const priceBucket = this.findPriceBucketForGuests(guestCount);

        const pricePerHour = Number(priceBucket.price_cents);

        const startDateTime = parseReservationDateTime(
            this._reservation.date,
            this._reservation.start_time
        );

        const endDateTime = parseReservationDateTime(
            this._reservation.date,
            this._reservation.end_time
        );

        const hours = (endDateTime.getTime() - startDateTime.getTime()) /
            (1000 * 60 * 60);

        const basePriceCents = Math.round(pricePerHour * hours);

        const pricePerGuestCents = Math.round(
            basePriceCents / guestCount
        );

        const platformFeeCents = 0;
        const taxCents = 0;

        const totalPriceCents = basePriceCents + platformFeeCents + taxCents;

        return {
            listingId: this._listingId,
            date: this._reservation.date,
            startTimeLabel: this._reservation.start_time,
            endTimeLabel: this._reservation.end_time,
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
            guestCount,
            pricePerGuestCents,
            hours,
            basePriceCents,
            platformFeeCents,
            taxCents,
            totalPriceCents,
            hostPayoutCents: basePriceCents,
            status: 'pending_payment',
            paymentStatus: 'unpaid'
        };
    }
    _bookingTracker = {
        summaryComplete: false, 
        guestDetailsComplete: false, 
        paymentInfoComplete: false
    };
    handleReserve() {
        const validationError = this.validateReservation();

        if (validationError) {
            this.showToast('Reservation Incomplete', validationError, 'error', 'dismissible');
            return;
        }

        this._bookingSummary = this.buildBookingSummary();
        this._hasAcceptedGuestWaiver = false;
        this._isBookingSummaryOpen = true;
    }


    handleCloseBookingSummary() {
        this._isBookingSummaryOpen = false;
        this._isGuestWaiverModalOpen = false;
        this._hasAcceptedGuestWaiver = false;
    }
    handleGuestWaiverAcceptanceChange(event) {
        this._hasAcceptedGuestWaiver = event.target.checked;
    }
    handleOpenGuestWaiver() {
        this._isGuestWaiverModalOpen = true;
    }
    handleCloseGuestWaiver() {
        this._isGuestWaiverModalOpen = false;
    }
    handleDismissPaymentCancelledNotice() {
        this._paymentCancelledDismissed = true;
    }
    tryOpenMessageHost() {
        if (
            !this._openMessageHost ||
            this._hasHandledOpenMessageHost ||
            !this._isLoaded ||
            !this._listing
        ) {
            return;
        }

        this._hasHandledOpenMessageHost = true;
        Promise.resolve().then(() => this.handleOpenHostMessage());
    }


    
    handleContinueGuestDetails() {
        const headerCmp = this.template.querySelector('x-get-wet-header-search-bar');

        if (headerCmp) {
            const guestDetails = headerCmp.getUserDetails();
            if (guestDetails) {
                console.log('here is guestDetails in handleContinueGuestDetails of getWetListingPage.js: ', JSON.stringify(guestDetails));
                console.log('here is guest info unstringified: ', guestDetails);
                this._reservation = {
                    ...this._reservation,
                    guest_first_name: guestDetails.first_name,
                    guest_last_name: guestDetails.last_name,
                    guest_email: guestDetails.email,
                    guest_id: guestDetails.id,
                    guest_phone: guestDetails.phone,
                    guest_country: guestDetails.country
                }
                this._bookingTracker = updateBookingTracker(this._bookingTracker);
                console.log('here is booking tracker after next updating successfully: ', JSON.stringify(this._bookingTracker))
                return;
            } 
        }
        this._bookingTracker = updateBookingTracker(this._bookingTracker, true);
        this.showToast('Please sign in to book.', '', 'warning');
    }
    handleContinueToPayment() {
        if (!this._hasAcceptedGuestWaiver) {
            this.showToast('Waiver required', 'Please review and accept the guest agreement before continuing to payment.', 'error', 'dismissible');
            return;
        }

        const payload = {
            listingId: this._bookingSummary.listingId,
            date: this._bookingSummary.date,
            startTime: this._bookingSummary.startTime,
            endTime: this._bookingSummary.endTime,
            guestCount: this._bookingSummary.guestCount,
            totalPriceCents: this._bookingSummary.totalPriceCents,
            platformFeeCents: this._bookingSummary.platformFeeCents,
            hostPayoutCents: this._bookingSummary.hostPayoutCents,
            waiverAccepted: true,
            waiverVersion: GUEST_WAIVER_VERSION,
            status: 'pending_payment',
            paymentStatus: 'unpaid'
        };

        console.log('Pending payment reservation payload in getWetListingPage.js:', JSON.stringify(payload));
        this._isBookingSummaryOpen = false;
        this.showToast('Payment not configured', 'Reservation details are ready for payment setup.', 'info', 'dismissible');
    }

    async handleContinueToPaymentProd() {
        try {
            if (!this._hasAcceptedGuestWaiver) {
                this.showToast('Waiver required', 'Please review and accept the guest agreement before continuing to payment.', 'error', 'dismissible');
                return;
            }

            const payload = {
                listingId: this._bookingSummary.listingId,
                listingPropertyName: this._listing?.property_name,
                listingAddress: this._listing?.formatted_address,
                listingStreet: this._listing?.street,
                listingCity: this._listing?.city,
                listingState: this._listing?.state,
                listingZipCode: this._listing?.zip_code,
                listingCountry: this._listing?.country,
                hostDisplayName: this._listing?.host_display_name || this._listing?.host_first_name,
                hostEmail: this._listing?.host_email,
                hostPhone: this._listing?.host_phone,
                date: this._bookingSummary.date,
                startTime: this._bookingSummary.startTime,
                endTime: this._bookingSummary.endTime,
                startTimeLabel: this._bookingSummary.startTimeLabel,
                endTimeLabel: this._bookingSummary.endTimeLabel,
                guestCount: this._bookingSummary.guestCount,
                totalPriceCents: this._bookingSummary.totalPriceCents,
                platformFeeCents: this._bookingSummary.platformFeeCents,
                hostPayoutCents: this._bookingSummary.hostPayoutCents,
                waiverAccepted: true,
                waiverVersion: GUEST_WAIVER_VERSION,
                status: 'pending_payment',
                paymentStatus: 'unpaid',
                guest_email: this._reservation.guest_email,
                guest_id: this._reservation.guest_id,
                guest_first_name: this._reservation.guest_first_name,
                guest_last_name: this._reservation.guest_last_name,
                guest_phone: this._reservation.guest_phone
            };

            console.log(
                'Pending payment reservation payload in getWetListingPage.js:',
                JSON.stringify(payload)
            );
            //return;

            const response = await fetch(
                `${API_BASE_URL}/stripe/checkout`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }
            );

            const data = await response.json();

            if (data.url) {
                window.location.href = data.url;
                return;
            }

            throw new Error(data.error || 'Failed to create Stripe checkout session');
        } catch (error) {
            console.error(
                'Error in handleContinueToPayment() of getWetListingPage.js:',
                error
            );

            this.showToast(
                'Payment Error',
                error?.message || 'Failed to initialize payment.',
                'error',
                'dismissible'
            );
        }
    }

    get endTimeDisabled() {
        return !this._reservation.start_time;
    }

    get showPaymentCancelledNotice() {
        return this._paymentCancelled && !this._paymentCancelledDismissed;
    }

    get hostMessageBody() {
        return this._hostMessageBody;
    }

    get hasAmenities() {
        return this._featuredRulesAndAmenities.length > 0;
    }

    get hasFullAmenities() {
        return this._amenityCategories.length > 0;
    }

    get hasRulesOrAmenities() {
        return this._houseRules.length > 0 || this.hasFullAmenities;
    }

    get isSendHostMessageDisabled() {
        return this._isSendingHostMessage || !this._hostMessageBody.trim();
    }

    handleOpenHostMessage() {
        if (!this.isAuthenticated) {
            this.dispatchEvent(new CustomEvent('loginrequired', {
                bubbles: true,
                composed: true
            }));
            return;
        }

        this._hostMessageBody = '';
        this._isHostMessageModalOpen = true;
    }

    handleCloseHostMessage() {
        if (this._isSendingHostMessage) return;

        this._isHostMessageModalOpen = false;
        this._hostMessageBody = '';
    }

    handleOpenRulesAmenitiesModal() {
        this._isRulesAmenitiesModalOpen = true;
    }

    handleCloseRulesAmenitiesModal() {
        this._isRulesAmenitiesModalOpen = false;
    }

    handleRulesAmenitiesBackdropClick(event) {
        if (event.target === event.currentTarget) {
            this.handleCloseRulesAmenitiesModal();
        }
    }

    handleHostMessageInput(event) {
        this._hostMessageBody = event.target.value;
    }

    async handleSendHostMessage() {
        const body = this._hostMessageBody.trim();
        if (!body) return;

        this._isSendingHostMessage = true;

        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            if (!idToken) {
                this.dispatchEvent(new CustomEvent('loginrequired', {
                    bubbles: true,
                    composed: true
                }));
                return;
            }

            const res = await fetch(`${API_BASE_URL}/cognito/messages/host`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    listingId: this._listingId,
                    body
                })
            });

            if (res.status === 401) {
                this.dispatchEvent(new CustomEvent('loginrequired', {
                    bubbles: true,
                    composed: true
                }));
                return;
            }

            if (!res.ok) {
                const errText = await res.text();
                throw new Error(`Failed to send message: ${res.status} ${errText}`);
            }

            this._isHostMessageModalOpen = false;
            this._hostMessageBody = '';
            this.showToast('Message sent', 'Your message was sent to the host.', 'success');
        } catch (error) {
            this.showToast('Error sending message', error?.message || error, 'error');
        } finally {
            this._isSendingHostMessage = false;
        }
    }

    async handleShowAllPhotos() {
        this._isGalleryModalOpen = true;
        this._galleryModalMode = 'grid';
        this._galleryModalLoading = !this._hasLoadedAllPhotos;
        await this.fetchAllPhotosIfNeeded();
    }
    
    async handleImageClick(event) {
        const photoKey = event.currentTarget?.dataset?.photoKey;
        if (!photoKey) return;

        this._isGalleryModalOpen = true;
        this._galleryModalMode = 'viewer';
        this._galleryModalLoading = !this._hasLoadedAllPhotos;

        await this.fetchAllPhotosIfNeeded();

        const galleryPhotos = this.galleryPhotos;
        const matchedIndex = galleryPhotos.findIndex((photo) => photo.s3_key === photoKey);
        this._activePhotoIndex = matchedIndex >= 0 ? matchedIndex : 0;
    }

    handleGridPhotoClick(event) {
        const photoIndex = Number(event.currentTarget?.dataset?.photoIndex);
        if (Number.isNaN(photoIndex)) return;

        this._activePhotoIndex = photoIndex;
        this._galleryModalMode = 'viewer';
    }

    handleCloseGallery() {
        this._isGalleryModalOpen = false;
        this._galleryModalMode = 'grid';
        this._galleryModalLoading = false;
    }

    handleBackdropClick(event) {
        if (event.target === event.currentTarget) {
            this.handleCloseGallery();
        }
    }

    handleOpenGridView() {
        this._galleryModalMode = 'grid';
    }

    get nextPrevDisabled() {
        return this._previewPhotos?.length <= 1
    }

    handleNext(event) {
        event.stopPropagation();
        if (this._previewPhotos.length <= 1) return;

        const photos = [...this._previewPhotos];
        const nextPrimary = photos.shift();

        if (!nextPrimary) return;

        photos.push(nextPrimary);
        this._previewPhotos = photos;
        this._primaryImageLoading = !!this._previewPhotos?.[0]?.loading;
    }

    handlePrev(event) {
        event.stopPropagation();

        if (this._previewPhotos.length <= 1) return;

        const photos = [...this._previewPhotos];
        const nextPrimary = photos.pop();

        if (!nextPrimary) return;

        photos.unshift(nextPrimary);
        this._previewPhotos = photos;
        this._primaryImageLoading = !!this._previewPhotos?.[0]?.loading;
    }

    handleViewerNext(event) {
        event?.stopPropagation();
        console.log('here is canNavigateGallery in handleViewerNext() of getWetListingPage.js: ', this.canNavigateGallery);
        if (!this.canNavigateGallery) {
            return;
        }

        this._activePhotoIndex = (this._activePhotoIndex + 1) % this.galleryPhotos.length;
    }

    handleViewerPrev(event) {
        event?.stopPropagation();
        if (!this.canNavigateGallery) {
            return;
        }

        this._activePhotoIndex = (
            this._activePhotoIndex - 1 + this.galleryPhotos.length
        ) % this.galleryPhotos.length;
    }

    handleWindowKeydown(event) {
        if (this._isRulesAmenitiesModalOpen && event.key === 'Escape') {
            this.handleCloseRulesAmenitiesModal();
            return;
        }

        if (!this._isGalleryModalOpen) return;

        if (event.key === 'Escape') {
            this.handleCloseGallery();
            return;
        }

        if (!this.isViewerMode) return;

        if (event.key === 'ArrowRight') {
            this.handleViewerNext();
        } else if (event.key === 'ArrowLeft') {
            this.handleViewerPrev();
        }
    }

    toastObj;

    showToast(title, message, variant, mode = null) {
        const modeP = mode
            ? mode
            : variant === 'success'
                ? 'dismissible'
                : 'sticky';

        this.toastObj = {
            variant: variant,
            mode: modeP,
            message: message,
            header: {
                text: title
            }
        };
    }
}
